import React, { Fragment, useState, useCallback, useMemo } from "react";

import dayjs from "dayjs";

import {
  changesSubslice,
  changesSlice,
  updateChanges,
} from "redux/features/changes";
import { RESOURCE_TYPE_PERSON } from "redux/typeConstants";

import { useAuthenticationState } from "redux/features/auth";

import { useSelector, useDispatch } from "react-redux";
import TrimmedInput from "common-frontend/util/trimmedInput";

import { Form } from "react-bulma-components";
const { Help } = Form;

export function usePhdYearState(id, section, profileData, occupationDetails) {
  const changeRecord = changesSubslice(
    useSelector(changesSlice.selector),
    id,
    section,
    RESOURCE_TYPE_PERSON,
    id
  );

  const changeKeys = useMemo(() => {
    return {
      id,
      section,
      resourceType: RESOURCE_TYPE_PERSON,
      resourceId: id,
    };
  }, [id, section]);

  const changes = changeRecord ? changeRecord.changes : {};

  const currentToDisplay = String(
    (changes.phd_year === undefined
      ? profileData.phd_year
      : changes.phd_year) ?? ""
  );

  const needed =
    occupationDetails.filter((o) => o.demographicsNeeded.phdOrFacultyYear)
      .length > 0;

  /* finished: initially null. After user has interacted with the widget
     and left it, is set to true. After a user has made a change and is
     still interacting, is set to false. */
  const [finished, setFinished] = useState(null);

  const allowFacultyAlternative =
    needed &&
    occupationDetails.filter((o) => o.demographicsNeeded.phdYear).length === 0;

  const { authenticatedAsStaff: amStaff } = useAuthenticationState();
  const whose = amStaff ? "this person's" : "your";

  const facultyAlternativePromptWording = allowFacultyAlternative
    ? ", or year of initial faculty appointment if no PhD"
    : "";
  const facultyAlternativeShortWording = allowFacultyAlternative
    ? " or initial faculty appointment"
    : "";

  const promptProps =
    needed || profileData.phd_year !== null
      ? {
          needed: true,
          heading:
            "Year of PhD" +
            facultyAlternativePromptWording +
            (needed ? "" : " (if applicable)"),
          currentToDisplay,
          finished,
          setFinished,
        }
      : { needed: false };
  const checkError = useCallback(
    (value) =>
      value === ""
        ? false
        : !(
            value.match(/^\d\d\d\d$/) &&
            value > "1900" &&
            value <= dayjs().add(1, "year").format("YYYY")
          ),
    []
  );

  const missing = needed && currentToDisplay === "";
  const error = checkError(currentToDisplay);
  promptProps.help = missing
    ? `Please specify ${whose} year of PhD` + facultyAlternativeShortWording
    : error
    ? "Please enter a valid 4-digit value for the year" +
      (needed ? "" : ", or leave it blank if not applicable")
    : null;

  const dispatch = useDispatch();

  promptProps.onChange = useCallback(
    (value) => {
      const storeValue =
        value === "" ? null : checkError(value) ? value : Number(value);

      updateChanges(
        dispatch,
        changeKeys,
        "phd_year",
        profileData.phd_year,
        storeValue
      );
    },
    [dispatch, changeKeys, checkError, profileData.phd_year]
  );

  const incomplete =
    missing || (error && finished === false)
      ? `Please specify ${whose} year of PhD` + facultyAlternativeShortWording
      : error
      ? `Please enter a valid value for ${whose} year of PhD` +
        facultyAlternativeShortWording +
        (needed ? "" : ", or leave it blank if not applicable")
      : null;

  return {
    promptProps,
    error,
    incomplete,
    finished,
    changeRecord,
    changeKeys,
  };
}

export function PromptPhdYear({ promptProps }) {
  const {
    currentToDisplay,
    onChange,
    help,
    finished,
    setFinished,
  } = promptProps;

  return (
    <Fragment>
      <TrimmedInput
        value={currentToDisplay}
        setValue={onChange}
        setFinished={setFinished}
        className="four-digits"
        maxLength="4"
        inputMode="numeric"
      />
      {finished && help ? <Help color="danger">{help}</Help> : null}
    </Fragment>
  );
}
